import { useCancelSubscription, useGetSubscription } from "@/api/stripe";
import {
  NESTED_DIALOG_Z,
  outlineDestructiveClassName,
  PaneSection,
  SettingRow,
  smButtonClassName,
} from "@/components/AdminPanel/Organization/components/manage-pane-primitives";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useIsSelfServeBilling } from "@/hooks/useIsSelfServeBilling";
import { cn } from "@/utils";
import {
  trackBillingSubscriptionCancelled,
  trackBillingSubscriptionReactivated,
} from "@/utils/analytics";
import { useState } from "react";
import toast from "react-hot-toast";

/**
 * The subscription's fate — cancel, and the undo that reverses it.
 *
 * Mounted on both Usage·Billing (beside the money mechanics) and
 * Settings·Organization (beside the org's other lifecycle verbs). One
 * component rather than two copies: a destructive billing verb with an
 * optimistic mutation behind it cannot be allowed to drift between surfaces.
 *
 * Cancel is reversible right up to the period end, so the row swaps to its
 * undo once taken rather than disappearing. Work is never on the table here;
 * billing is.
 */
export function SubscriptionSection() {
  const { data: subscription } = useGetSubscription();
  const cancelSubscription = useCancelSubscription();
  const isSelfServeBilling = useIsSelfServeBilling();

  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);

  const plan = subscription?.data.stripe_subscription;
  const isScheduledForCancellation =
    !!plan?.cancel_at && plan?.status !== "canceled";

  const asDate = (value: string | null | undefined) =>
    value
      ? new Date(value).toLocaleDateString(undefined, {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : null;
  const cancelDate = asDate(plan?.cancel_at);
  const periodEnd = asDate(plan?.current_period_end);

  // Enterprise and beta plans are managed for the customer, so the verb is
  // not theirs to press.
  if (!plan || !isSelfServeBilling) return null;

  const setCancellation = async (cancel: boolean) => {
    try {
      await cancelSubscription.mutateAsync(cancel);
      const payload = {
        planName: plan.plan_name,
        billingInterval: plan.billing_interval,
        seatCount: plan.seat_count,
      };
      if (cancel) {
        trackBillingSubscriptionCancelled(payload);
        setShowCancelDialog(false);
        toast.success(
          "Subscription cancelled. You keep access until the period ends.",
        );
      } else {
        trackBillingSubscriptionReactivated(payload);
        setShowReactivateDialog(false);
        toast.success("Subscription reactivated.");
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to update the subscription.",
      );
    }
  };

  return (
    <>
      <PaneSection head="Subscription">
        {isScheduledForCancellation ? (
          <SettingRow
            label={`Subscription ends ${cancelDate}`}
            desc="Your work stays readable after that; agents and editing stop."
          >
            <Button
              size="sm"
              variant="secondary"
              className={smButtonClassName}
              onClick={() => setShowReactivateDialog(true)}
              disabled={cancelSubscription.isPending}
            >
              Keep subscription
            </Button>
          </SettingRow>
        ) : (
          <SettingRow
            label="Cancel subscription"
            desc={
              periodEnd
                ? `Runs through ${periodEnd}, then ends. Your work stays readable.`
                : "Runs to the end of the period, then ends. Your work stays readable."
            }
          >
            <Button
              size="sm"
              variant="ghost"
              className={cn(smButtonClassName, outlineDestructiveClassName)}
              onClick={() => setShowCancelDialog(true)}
              disabled={cancelSubscription.isPending}
            >
              Cancel…
            </Button>
          </SettingRow>
        )}
      </PaneSection>

      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent
          className={NESTED_DIALOG_Z}
          overlayClassName={NESTED_DIALOG_Z}
        >
          <DialogHeader>
            <DialogTitle>Cancel the subscription?</DialogTitle>
            <DialogDescription>
              {periodEnd
                ? `The plan runs through ${periodEnd}, then ends.`
                : "The plan runs to the end of the period, then ends."}{" "}
              Your work stays readable; agents and editing stop until you
              resubscribe.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCancelDialog(false)}>
              Keep subscription
            </Button>
            <Button
              className="bg-spectrum-red text-paper hover:bg-[color-mix(in_oklab,var(--spectrum-red)_88%,var(--ink))]"
              disabled={cancelSubscription.isPending}
              isLoading={cancelSubscription.isPending}
              onClick={() => setCancellation(true)}
            >
              Cancel subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showReactivateDialog}
        onOpenChange={setShowReactivateDialog}
      >
        <DialogContent
          className={NESTED_DIALOG_Z}
          overlayClassName={NESTED_DIALOG_Z}
        >
          <DialogHeader>
            <DialogTitle>Keep the subscription running?</DialogTitle>
            <DialogDescription>
              Billing continues as before and nothing stops on {cancelDate}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowReactivateDialog(false)}
            >
              Leave it ending
            </Button>
            <Button
              disabled={cancelSubscription.isPending}
              isLoading={cancelSubscription.isPending}
              onClick={() => setCancellation(false)}
            >
              Keep subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
